import Link from "next/link";
import Slider, { Settings } from "react-slick";
import { UrlObject } from "url";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faFire, faClock, faStar, faSearch, faUsers,
    faEye, faHandshake, faDownload, faBuilding, faIndustry
} from "@fortawesome/free-solid-svg-icons";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { useCallback, useMemo, useRef } from "react";

type MoreLinkItemProps = {
    href: string | UrlObject;
    icon: IconProp;
    label: string;
};

const MoreLinkItem = ({ href, icon, label }: MoreLinkItemProps) => (
    <Link href={href} className="flex items-center gap-2 text-sm mb-2.5">
        <FontAwesomeIcon icon={icon} className="w-6 h-6 text-[var(--primary-color)]" />
        {label}
    </Link>
);

const slides = [
    {
        title: null, items: [
            { href: { pathname: "/product", query: { scope: "special" } }, icon: faFire, label: "What's Hot" },
            { href: { pathname: "/product", query: { scope: "featured" } }, icon: faClock, label: "Ending Soon" },
            { href: { pathname: "/product", query: { scope: "featured" } }, icon: faStar, label: "What's New" },
            { href: { pathname: "/product", query: { scope: "normal" } }, icon: faSearch, label: "Top Searches" },
            { href: { pathname: "/product", query: { scope: "special" } }, icon: faUsers, label: "Recent Buyers" },
        ]
    },
    {
        title: null, items: [
            { href: { pathname: "/spare-shops-near-me" }, icon: faEye, label: "Most Viewed" },
            { href: { pathname: "/spare-shops-near-me" }, icon: faHandshake, label: "Most interactive" },
        ]
    },
    {
        title: "Download", items: [
            { href: { pathname: "/product", query: { scope: "normal" } }, icon: faBuilding, label: "Most Viewed" },
            { href: { pathname: "/product", query: { scope: "featured" } }, icon: faIndustry, label: "Frequent downloaded" },
            { href: { pathname: "/product", query: { scope: "special" } }, icon: faDownload, label: "Most downloaded" },
        ]
    },
];

const focusableSel =
    'a[href],area[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

export default function MoreLinks() {
    const rootRef = useRef<HTMLDivElement>(null);

    const updateA11y = useCallback(() => {
        const root = rootRef.current;
        if (!root) return;

        root.querySelectorAll<HTMLElement>(".slick-slide").forEach((slide) => {
            const hidden = slide.getAttribute("aria-hidden") === "true";

            slide.querySelectorAll<HTMLElement>(focusableSel).forEach((el) => {
                if (hidden) {
                    el.setAttribute("tabindex", "-1");
                    el.setAttribute("aria-hidden", "true");
                    el.setAttribute("data-a11y-managed", "1");
                } else if (el.getAttribute("data-a11y-managed") === "1") {
                    el.removeAttribute("tabindex");
                    el.removeAttribute("aria-hidden");
                    el.removeAttribute("data-a11y-managed");
                }
            });

            // Bonus: block AT & focus entirely where supported
            if (hidden) slide.setAttribute('inert', '');
            else slide.removeAttribute('inert');
        });
    }, []);

    const sliderSettings: Settings = useMemo(() => ({
        dots: true,
        infinite: true,
        speed: 600,
        slidesToShow: 1,
        slidesToScroll: 1,
        autoplay: false,
        arrows: false,
        accessibility: true,
        onInit: updateA11y,
        onReInit: updateA11y,
        afterChange: () => updateA11y(),
    }), [updateA11y]);

    return (
        <div ref={rootRef} className="h-full border border-gray-300 p-4 xl:p-2 2xl:p-3 pb-[50px]">
            <Slider {...sliderSettings}>
                {slides.map((slide, index) => (
                    <div key={index}>
                        {slide.title && (
                            <p className="text-md xl:text-sm 2xl:text-lg font-semibold mb-1 max-xl:mb-2 2xl:mb-2">
                                {slide.title}
                            </p>
                        )}
                        <div className="columns-2">
                            {slide.items.map((item, idx) => <MoreLinkItem key={idx} {...item} />)}
                        </div>
                    </div>
                ))}
            </Slider>
        </div>
    );
}
